/**
 * MUTAGIM.SHOP — Admin AI Prompts Page
 * Configuration et édition des prompts IA utilisés pour la génération de contenu
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Loader2,
  Sparkles,
  Save,
  RotateCcw,
  Check,
  Package,
  FolderOpen,
  Image as ImageIcon,
  Wand2,
  Info
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par catégorie
const categoryIcons = {
  product: Package,
  category: FolderOpen,
  slider: ImageIcon,
  other: Wand2
};

// Couleurs par catégorie
const categoryColors = {
  product: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  category: 'bg-green-500/20 text-green-400 border-green-500/50',
  slider: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  other: 'bg-orange-500/20 text-orange-400 border-orange-500/50'
};

export function AdminAIPromptsPage() {
  const { isLight } = useAdminTheme();
  const token = localStorage.getItem('admin_token');
  
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [promptToReset, setPromptToReset] = useState(null);

  // Charger les prompts
  const fetchPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/ai-prompts/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPrompts(data.prompts || []);
        
        // Sélectionner le premier prompt par défaut
        if (data.prompts?.length > 0 && !selectedPrompt) {
          setSelectedPrompt(data.prompts[0]);
          setEditedPrompt(data.prompts[0].system_prompt);
        }
      }
    } catch (err) {
      console.error('Error fetching prompts:', err);
      toast.error('Erreur lors du chargement des prompts');
    } finally {
      setLoading(false);
    }
  }, [token, selectedPrompt]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // Sauvegarder un prompt
  const handleSave = async () => {
    if (!selectedPrompt) return;
    
    try {
      setSaving(selectedPrompt.key);
      
      const res = await fetch(`${API_URL}/api/ai-prompts/admin/${selectedPrompt.key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          system_prompt: editedPrompt
        })
      });
      
      if (res.ok) {
        toast.success('Prompt sauvegardé avec succès !');
        
        // Mettre à jour l'état local
        setPrompts(prev => prev.map(p => 
          p.key === selectedPrompt.key 
            ? { ...p, system_prompt: editedPrompt, is_custom: true }
            : p
        ));
        setSelectedPrompt(prev => ({ ...prev, system_prompt: editedPrompt, is_custom: true }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Error saving prompt:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  // Réinitialiser un prompt
  const handleReset = async () => {
    if (!promptToReset) return;
    
    try {
      setSaving(promptToReset.key);
      
      const res = await fetch(`${API_URL}/api/ai-prompts/admin/${promptToReset.key}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Prompt réinitialisé !');
        setResetDialogOpen(false);
        setPromptToReset(null);
        fetchPrompts();
      } else {
        toast.error('Erreur lors de la réinitialisation');
      }
    } catch (err) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setSaving(null);
    }
  };

  // Sélectionner un prompt
  const selectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setEditedPrompt(prompt.system_prompt);
  };

  // Grouper les prompts par catégorie
  const groupedPrompts = prompts.reduce((acc, prompt) => {
    const cat = prompt.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(prompt);
    return acc;
  }, {});

  if (loading) {
    return (
      <AdminPageWrapper title="Configuration IA" subtitle="Chargement...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper 
      title="Configuration IA" 
      subtitle="Personnalisez les prompts utilisés par l'IA pour générer du contenu"
    >
      <div className="space-y-6" data-testid="admin-ai-prompts-page">
        {/* Info Banner */}
        <div className={cn(
          "p-4 rounded-lg border flex items-start gap-3",
          isLight 
            ? "bg-blue-50 border-blue-200" 
            : "bg-blue-950/30 border-blue-900/50"
        )}>
          <Info className={cn("w-5 h-5 mt-0.5", isLight ? "text-blue-600" : "text-blue-400")} />
          <div className={cn("text-sm", isLight ? "text-blue-800" : "text-blue-300")}>
            <p className="font-medium mb-1">Comment ça marche ?</p>
            <p className="opacity-80">
              Ces prompts sont les instructions envoyées à l'IA lors de la génération de contenu.
              Modifiez-les pour adapter le style, le ton et le format des résultats à vos besoins.
              Les prompts personnalisés sont marqués d'un badge "Personnalisé".
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des prompts */}
          <div className="lg:col-span-1 space-y-4">
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader className="pb-3">
                <CardTitle className={cn("text-lg", isLight ? "text-gray-900" : "text-white")}>
                  Prompts disponibles
                </CardTitle>
                <CardDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
                  Sélectionnez un prompt à éditer
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => {
                  const Icon = categoryIcons[category] || Wand2;
                  return (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium opacity-60">
                        <Icon className="w-3.5 h-3.5" />
                        {category === 'product' && 'Produits'}
                        {category === 'category' && 'Catégories'}
                        {category === 'slider' && 'Slider'}
                        {category === 'other' && 'Autres'}
                      </div>
                      <div className="space-y-1">
                        {categoryPrompts.map((prompt) => (
                          <button
                            key={prompt.key}
                            onClick={() => selectPrompt(prompt)}
                            className={cn(
                              "w-full text-left p-3 rounded-lg border transition-all",
                              selectedPrompt?.key === prompt.key
                                ? isLight 
                                  ? "border-indigo-500 bg-indigo-50"
                                  : "border-indigo-500 bg-indigo-500/10"
                                : isLight
                                  ? "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                  : "border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50"
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <span className={cn(
                                "font-medium text-sm",
                                isLight ? "text-gray-900" : "text-white"
                              )}>
                                {prompt.name}
                              </span>
                              {prompt.is_custom && (
                                <Badge className="text-[10px] px-1.5 py-0 bg-amber-500/20 text-amber-400 border border-amber-500/50">
                                  Personnalisé
                                </Badge>
                              )}
                            </div>
                            <p className={cn(
                              "text-xs mt-1 line-clamp-1",
                              isLight ? "text-gray-500" : "text-gray-500"
                            )}>
                              {prompt.description}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Éditeur de prompt */}
          <div className="lg:col-span-2">
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      {selectedPrompt?.name || 'Sélectionnez un prompt'}
                    </CardTitle>
                    <CardDescription className={cn("mt-1", isLight ? "text-gray-600" : "text-gray-400")}>
                      {selectedPrompt?.description || 'Choisissez un prompt dans la liste pour l\'éditer'}
                    </CardDescription>
                  </div>
                  {selectedPrompt && (
                    <Badge className={cn("border", categoryColors[selectedPrompt.category || 'other'])}>
                      {selectedPrompt.category === 'product' && 'Produit'}
                      {selectedPrompt.category === 'category' && 'Catégorie'}
                      {selectedPrompt.category === 'slider' && 'Slider'}
                      {selectedPrompt.category === 'other' && 'Autre'}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedPrompt ? (
                  <>
                    <div className="space-y-2">
                      <label className={cn(
                        "text-sm font-medium",
                        isLight ? "text-gray-700" : "text-gray-300"
                      )}>
                        Instructions système (System Prompt)
                      </label>
                      <Textarea
                        value={editedPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        rows={16}
                        className={cn(
                          "font-mono text-sm resize-none",
                          isLight 
                            ? "bg-white border-gray-300" 
                            : "bg-zinc-800 border-zinc-700 text-white"
                        )}
                        placeholder="Instructions pour l'IA..."
                      />
                      <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                        {editedPrompt.length} caractères
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setPromptToReset(selectedPrompt);
                                setResetDialogOpen(true);
                              }}
                              disabled={!selectedPrompt.is_custom}
                              className={cn(
                                isLight ? "border-gray-300" : "border-zinc-700",
                                !selectedPrompt.is_custom && "opacity-50"
                              )}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Réinitialiser
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Rétablir le prompt par défaut</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <Button
                        onClick={handleSave}
                        disabled={saving === selectedPrompt.key || editedPrompt === selectedPrompt.system_prompt}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        {saving === selectedPrompt.key ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Sauvegarder
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className={cn(
                    "flex flex-col items-center justify-center py-12 text-center",
                    isLight ? "text-gray-500" : "text-gray-500"
                  )}>
                    <Wand2 className="w-12 h-12 mb-4 opacity-50" />
                    <p>Sélectionnez un prompt dans la liste</p>
                    <p className="text-sm">pour commencer à l'éditer</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips */}
            {selectedPrompt && (
              <Card className={cn(
                "mt-4 border",
                isLight ? "bg-amber-50 border-amber-200" : "bg-amber-950/30 border-amber-900/50"
              )}>
                <CardContent className="p-4">
                  <h4 className={cn(
                    "font-medium text-sm mb-2",
                    isLight ? "text-amber-800" : "text-amber-400"
                  )}>
                    Conseils pour de meilleurs résultats
                  </h4>
                  <ul className={cn(
                    "text-xs space-y-1",
                    isLight ? "text-amber-700" : "text-amber-300/80"
                  )}>
                    <li>• Soyez précis et spécifique dans vos instructions</li>
                    <li>• Utilisez des exemples concrets quand c'est possible</li>
                    <li>• Définissez clairement le format de sortie attendu</li>
                    <li>• Ajoutez des contraintes (longueur max, ton, etc.)</li>
                    <li>• Testez vos modifications avec plusieurs produits</li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Dialog de confirmation de réinitialisation */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Réinitialiser le prompt ?
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Cette action va rétablir le prompt "{promptToReset?.name}" à sa valeur par défaut.
              Toutes vos modifications seront perdues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button
              onClick={handleReset}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminAIPromptsPage;
